import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronRight, FiEdit2, FiEye, FiLayers, FiPlus, FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import { hasScreenAccess } from "../../utils/permissions";
import {
  createAccountPlanType,
  getAccountPlanType,
  listAccountPlanTypes,
  updateAccountPlanType,
} from "../../service/CallCenterAccountPlanTypes";
import { getAccountPlanMetadata } from "../../service/CallCenterAccountPlans";
import AccountPlanTypeFormModal, {
  buildAccountPlanTypePayload,
} from "../../components/call-center/AccountPlanTypeFormModal";
import AccountPlanTypeDetailDrawer from "../../components/call-center/AccountPlanTypeDetailDrawer";
import ListingEmptyState from "../../components/call-center/listing/ListingEmptyState";
import ListingFilterHeader from "../../components/call-center/listing/ListingFilterHeader";
import ListingPaginationFooter from "../../components/call-center/listing/ListingPaginationFooter";
import StatusBadge from "../../components/call-center/listing/StatusBadge";
import TableSkeleton from "../../components/call-center/listing/TableSkeleton";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_ICON_ACTION_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "../../components/call-center/listing/listingStyles";
import {
  formatAccountPlanAssignmentMode,
  formatAccountPlanPriority,
  formatAccountPlanReminder,
  formatAccountPlanScheduleSummary,
  priorityStatusCode,
} from "../../utils/accountPlanLabels";

const PAGE_SIZE = 20;

const UsageMeter = ({ active, total }) => {
  const safeTotal = Math.max(Number(total) || 0, 0);
  const safeActive = Math.min(Math.max(Number(active) || 0, 0), safeTotal || 0);
  const percent = safeTotal > 0 ? Math.round((safeActive / safeTotal) * 100) : 0;

  return (
    <div className="min-w-[108px]">
      <div className="mb-1 text-xs font-medium text-slate-700">
        {safeActive}
        <span className="text-slate-400"> / </span>
        {safeTotal}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-[#2B7FFF] transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const CallCenterAccountPlanTypesPage = () => {
  const t = useCallCenterT();
  const canCreate = hasScreenAccess("CC_ACCOUNT_PLAN", "canCreate");
  const canUpdate = hasScreenAccess("CC_ACCOUNT_PLAN", "canUpdate");

  const [planTypes, setPlanTypes] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPlanType, setEditingPlanType] = useState(null);
  const [detailPlanType, setDetailPlanType] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [types, meta] = await Promise.all([
        listAccountPlanTypes(),
        getAccountPlanMetadata(),
      ]);
      setPlanTypes(Array.isArray(types) ? types : []);
      setMetadata(meta || null);
    } catch (loadError) {
      const message = loadError?.message || t("ccAccountPlanTypeLoadFailed");
      setError(message);
      toast.error(message);
      setPlanTypes([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const reminderOptions = metadata?.reminderOptions || [];
  const agentOptions = metadata?.agents || [];

  const filteredPlanTypes = useMemo(() => {
    const query = search.trim().toLowerCase();
    return planTypes.filter((planType) => {
      if (query) {
        const haystack = `${planType.name || ""} ${planType.description || ""}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (activeFilter === "active" && !planType.active) return false;
      if (activeFilter === "inactive" && planType.active) return false;
      return true;
    });
  }, [planTypes, search, activeFilter]);

  const totalElements = filteredPlanTypes.length;
  const totalPages = Math.max(Math.ceil(totalElements / PAGE_SIZE), totalElements > 0 ? 1 : 0);
  const pagedPlanTypes = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredPlanTypes.slice(start, start + PAGE_SIZE);
  }, [filteredPlanTypes, page]);

  useEffect(() => {
    setPage(0);
  }, [search, activeFilter]);

  const hasActiveFilters = Boolean(search || activeFilter);
  const showFilteredEmpty = !loading && planTypes.length > 0 && filteredPlanTypes.length === 0;

  const openCreate = () => {
    setEditingPlanType(null);
    setFormOpen(true);
  };

  const openEdit = (planType) => {
    setDetailPlanType(null);
    setEditingPlanType(planType);
    setFormOpen(true);
  };

  const openDetail = async (planType) => {
    if (!planType?.id) return;
    setDetailPlanType(planType);
    setDetailLoading(true);
    try {
      const fresh = await getAccountPlanType(planType.id);
      setDetailPlanType(fresh || planType);
    } catch (loadError) {
      toast.error(loadError?.message || t("ccAccountPlanTypeLoadFailed"));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailPlanType(null);
    setDetailLoading(false);
  };

  const handleSubmit = async (values) => {
    const payload = buildAccountPlanTypePayload(values);
    try {
      setSaving(true);
      if (editingPlanType?.id) {
        await updateAccountPlanType(editingPlanType.id, payload);
        toast.success(t("ccAccountPlanTypeUpdated"));
      } else {
        await createAccountPlanType(payload);
        toast.success(t("ccAccountPlanTypeCreated"));
      }
      setFormOpen(false);
      setEditingPlanType(null);
      await loadData();
    } catch (submitError) {
      toast.error(submitError?.message || t("ccAccountPlanTypeSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const applyFilters = () => setSearch(searchInput.trim());
  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setActiveFilter("");
    setPage(0);
  };

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <nav className="mb-2 flex items-center gap-1 text-xs font-medium text-slate-500">
              <Link to="/call-center/account-plans" className="hover:text-[#2B7FFF]">
                {t("ccAccountPlanTypeBreadcrumbPaymentPlans")}
              </Link>
              <FiChevronRight className="h-3.5 w-3.5" />
              <span className="text-slate-700">{t("ccAccountPlanTypesPageTitle")}</span>
            </nav>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">
              {t("ccAccountPlanTypesPageTitle")}
            </h1>
            <p className="mt-1 text-sm text-[#6b7280]">{t("ccAccountPlanTypesPageSubtitle")}</p>
          </div>
          {canCreate ? (
            <button type="button" onClick={openCreate} className={LISTING_PRIMARY_BUTTON_CLASS}>
              <FiPlus className="h-4 w-4" />
              {t("ccAccountPlanTypeCreate")}
            </button>
          ) : null}
        </header>

        <div className="mb-6 rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">{t("ccAccountPlanTypeInfoTitle")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("ccAccountPlanTypeInfoBody")}</p>
        </div>

        {error ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{error}</span>
            <button type="button" onClick={loadData} className="font-semibold hover:underline">
              {t("ccRefresh")}
            </button>
          </div>
        ) : null}

        <section className={LISTING_FILTER_CARD_CLASS}>
          <ListingFilterHeader
            title={t("ccListFiltersTitle")}
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            resultsLabel={t("ccAccountPlanTypeResultsLabel")}
            showingPageLabel={t("ccShowingPage")}
            ofLabel={t("ccOf")}
            refreshLabel={t("ccRefresh")}
            loading={loading}
            onRefresh={loadData}
            actions={
              hasActiveFilters ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  {t("ccClearFilters")}
                </button>
              ) : null
            }
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className={FILTER_LABEL_CLASS}>{t("ccSearch")}</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") applyFilters();
                    }}
                    placeholder={t("ccAccountPlanTypeSearchPlaceholder")}
                    className={`${FILTER_FIELD_CLASS} pl-9`}
                  />
                </div>
                <button type="button" onClick={applyFilters} className={LISTING_PRIMARY_BUTTON_CLASS}>
                  {t("ccSearch")}
                </button>
              </div>
            </div>
            <div>
              <label className={FILTER_LABEL_CLASS}>{t("ccStatus")}</label>
              <select
                value={activeFilter}
                onChange={(event) => setActiveFilter(event.target.value)}
                className={FILTER_FIELD_CLASS}
              >
                <option value="">{t("ccAll")}</option>
                <option value="active">{t("ccActive")}</option>
                <option value="inactive">{t("ccInactive")}</option>
              </select>
            </div>
          </div>
        </section>

        <section className={`${LISTING_CARD_CLASS} mt-6`}>
          {loading ? (
            <div className="p-4">
              <TableSkeleton columns={7} rows={6} />
            </div>
          ) : showFilteredEmpty ? (
            <ListingEmptyState
              icon={FiSearch}
              title={t("ccAccountPlanTypeEmptyFilteredTitle")}
              hint={t("ccAccountPlanTypeEmptyFilteredHint")}
              action={
                <button
                  type="button"
                  onClick={resetFilters}
                  className={`mt-4 ${LISTING_PRIMARY_BUTTON_CLASS}`}
                >
                  {t("ccClearFilters")}
                </button>
              }
            />
          ) : !pagedPlanTypes.length ? (
            <ListingEmptyState
              icon={FiLayers}
              title={t("ccAccountPlanTypesEmptyTitle")}
              hint={t("ccAccountPlanTypesEmptyHint")}
              action={
                canCreate ? (
                  <button type="button" onClick={openCreate} className={`mt-4 ${LISTING_PRIMARY_BUTTON_CLASS}`}>
                    <FiPlus className="h-4 w-4" />
                    {t("ccAccountPlanTypeCreate")}
                  </button>
                ) : null
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={LISTING_TABLE_HEAD_CLASS}>
                    <tr>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccAccountPlanTypeName")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccAccountPlanTypeScheduleColumn")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccAccountPlanReminderOffset")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccAccountPlanAssignmentMode")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccAccountPlanTypeUsage")}</th>
                      <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccStatus")}</th>
                      <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} text-right`}>{t("ccActions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedPlanTypes.map((planType) => (
                      <tr
                        key={planType.id}
                        className={`${LISTING_TABLE_ROW_CLASS} cursor-pointer`}
                        onClick={() => openDetail(planType)}
                      >
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetail(planType);
                            }}
                            className="text-left"
                          >
                            <div className="font-semibold text-slate-900 hover:text-[#2B7FFF]">
                              {planType.name}
                            </div>
                            {planType.description ? (
                              <div className="mt-0.5 max-w-xs truncate text-xs text-slate-500">
                                {planType.description}
                              </div>
                            ) : null}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">
                            {formatAccountPlanScheduleSummary(planType, metadata, t)}
                          </div>
                          <div className="mt-1">
                            <StatusBadge
                              status={priorityStatusCode(planType.ticketPriority)}
                              label={formatAccountPlanPriority(planType.ticketPriority, metadata)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatAccountPlanReminder(planType.reminderDaysBefore, metadata)}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatAccountPlanAssignmentMode(planType.assignmentMode, metadata, t)}
                          {planType.defaultAssignedToName ? (
                            <div className="mt-0.5 text-xs text-slate-400">
                              {planType.defaultAssignedToName}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <UsageMeter
                            active={planType.activeAccountPlanCount}
                            total={planType.totalAccountPlanCount}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            status={planType.active ? "ACTIVE" : "CANCELLED"}
                            label={planType.active ? t("ccActive") : t("ccInactive")}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDetail(planType);
                              }}
                              className={LISTING_ICON_ACTION_CLASS}
                              aria-label={t("ccViewDetails")}
                            >
                              <FiEye className="h-4 w-4" />
                            </button>
                            {canUpdate ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEdit(planType);
                                }}
                                className={LISTING_ICON_ACTION_CLASS}
                                aria-label={t("ccEdit")}
                              >
                                <FiEdit2 className="h-4 w-4" />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListingPaginationFooter
                totalElements={totalElements}
                resultsLabel={t("ccAccountPlanTypeResultsLabel")}
                page={page}
                totalPages={totalPages}
                loading={loading}
                onPrevious={() => setPage((current) => Math.max(current - 1, 0))}
                onNext={() => setPage((current) => current + 1)}
                previousLabel={t("ccPrevious")}
                nextLabel={t("ccNext")}
              />
            </>
          )}
        </section>
      </div>

      <AccountPlanTypeFormModal
        open={formOpen}
        editingPlanType={editingPlanType}
        metadata={metadata}
        reminderOptions={reminderOptions}
        agentOptions={agentOptions}
        saving={saving}
        onClose={() => {
          setFormOpen(false);
          setEditingPlanType(null);
        }}
        onSubmit={handleSubmit}
        t={t}
      />

      <AccountPlanTypeDetailDrawer
        open={Boolean(detailPlanType)}
        planType={detailPlanType}
        loading={detailLoading}
        metadata={metadata}
        canUpdate={canUpdate}
        onClose={closeDetail}
        onEdit={(planType) => {
          closeDetail();
          openEdit(planType);
        }}
        t={t}
      />
    </div>
  );
};

export default CallCenterAccountPlanTypesPage;
